import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
} from "@chakra-ui/react";
import { TabHeader } from "components/TabHeader";
import { CloseButton } from "components/RFQ/CloseButton";
import apiV2, { getErrorMessage } from "api-v2";
import { showToast } from "utils/helpers";
import EmailPreview from "pages/EmailPreview/EmailPreview";
import { RFxInvitationEmailContent } from "components/email-template/RFxInvitationEmailContent";
import { SupplierSelector } from "./components/SupplierSelector";
import { GuestSelector } from "./components/GuestSelector";
import { useRfxMail } from "components/email-template/hooks/useRfxMail";
import { isRfxOnboardingType } from "newui/features/Rfx/utils";
import { OnboardingRFxInvitationEmailContent } from "components/email-template/Onboarding/OnboardingRFxInvitationEmailContent";

const InviteSupplierDrawer = ({
	rfqName,
	onClose,
	isOpen,
	rfqId,
	rfqDetail,
	unCheckedList,
	inviteSupplierHandler,
	isGuest = false,
	...props
}) => {
	const {
		rfxMail,
		additionalMessage,
		handleSupplierNameChange,
		handleAdditionalMessageChange,
	} = useRfxMail(rfqDetail);

	const handleSupplierChange = (e) => {
		handleSupplierNameChange(e.label);
	};

	const handleMessageChange = (e) => {
		handleAdditionalMessageChange(e.target.value);
	};

	const handleRFQActionSubmit = async (values) => {
		try {
			const payload = {
				additional_message: values.additional_message,
			};

			if (isGuest) {
				// separate suppliers and regular guests
				const supplier_ids = values.fieldData
					.filter((guest) => guest.supplier_id !== null)
					.map((guest) => guest.supplier_id);

				const regularGuests = values.fieldData
					.filter((guest) => guest.supplier_id === null)
					.map(({ name, email }) => ({ name, email }));

				if (supplier_ids.length > 0) {
					payload.supplier_ids = supplier_ids;
				}

				if (regularGuests.length > 0) {
					payload.guests = regularGuests;
				}
			} else {
				payload.supplier_ids = [values.supplier];
			}

			let res = await apiV2.post(`rfq/${rfqId}/invite-supplier`, payload);
			if (res.status === 200) {
				showToast(res?.data?.message, res?.data?.title, true);
				inviteSupplierHandler(res.data.data?.supplierInvited);
				onClose();
			}
		} catch (err) {
			showToast(getErrorMessage(err), "Error");
		}
	};

	const header = "Invite " + (isGuest ? "Guest" : "Supplier");
	const headerDescription =
		`Invite ${isGuest ? "guests" : "suppliers"} to RFx “${rfqName}”` +
		(!isGuest && " from your Supplier List.");

	const isOnboardingRFx = isRfxOnboardingType(
		rfqDetail?.rfxType || rfqDetail?.type
	);

	return (
		<Drawer
			placement={"right"}
			isOpen={isOpen}
			onClose={onClose}
			size="xl"
			{...props}
		>
			<DrawerOverlay onClick={onClose} />
			<DrawerContent maxWidth={"1280px"}>
				<DrawerHeader
					className="!px-0 py-6 text-black"
					borderBottomWidth="1px"
				>
					<div className="mx-6">
						<TabHeader
							noDivider={true}
							heading={header}
							description={headerDescription}
						>
							<CloseButton onClose={onClose} />
						</TabHeader>
					</div>
				</DrawerHeader>
				<DrawerBody className="max-h-[calc(100vh - 100px)] p-6">
					<div className="grid grid-cols-3">
						<div className="space-y-4 py-4">
							{isGuest ? (
								<GuestSelector
									additionalMessage={additionalMessage}
									handleAdditionalMessageChange={handleMessageChange}
									handleSubmit={handleRFQActionSubmit}
									inviteSupplierHandler={inviteSupplierHandler}
									onClose={onClose}
									rfqId={rfqId}
									supplierListUuid={rfqDetail?.supplierList?.uuid}
								/>
							) : (
								<SupplierSelector
									additionalMessage={additionalMessage}
									supplierList={rfqDetail?.supplierList}
									unCheckedList={unCheckedList}
									handleAdditionalMessageChange={handleMessageChange}
									handleSubmit={handleRFQActionSubmit}
									handleSupplierChange={handleSupplierChange}
								/>
							)}
						</div>
						<div className="col-span-2">
							<EmailPreview>
								{isOnboardingRFx ? (
									<OnboardingRFxInvitationEmailContent rfxMail={rfxMail} />
								) : (
									<RFxInvitationEmailContent rfxMail={rfxMail} />
								)}
							</EmailPreview>
						</div>
					</div>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};

export default InviteSupplierDrawer;
